<template>
  <div>
    <div class="modal-header text-center">
      <h4 class="modal-title max-lg:hidden">
        Inscription
        <a v-if="user.role">{{ roleName }}</a>
      </h4>
      <FormTitle view="signup" class="lg:hidden" />

      <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
    </div>
    <div class="p-6">
      <form novalidate="novalidate" @submit="signup">
        <fieldset v-if="!user.role">
          <span class="registration-tile-label mb-6">Quel type de compte voulez-vous créer&nbsp;?</span>
          <div class="grid gap-4">
            <button
              class="group flex items-center justify-center gap-4 rounded-lg border-0 bg-neutral-400/5 p-6 text-left text-lg font-light text-neutral-700 ring-2 ring-neutral-200 transition-colors hover:bg-primary/5 hover:text-primary hover:ring-primary max-sm:px-4 sm:gap-6"
              @click="user.role = 'client'"
            >
              <FaIcon type="fal" icon="user" class="text-3xl" />
              <div>
                <p class="m-0">Client</p>
                <p class="m-0 text-sm text-neutral-400">J'ai un projet à réaliser</p>
              </div>
              <div class="grow text-right">
                <FaIcon type="fal" icon="chevron-right" />
              </div>
            </button>
            <button
              class="group flex items-center justify-center gap-4 rounded-lg border-0 bg-neutral-400/5 p-6 text-left text-lg font-light text-neutral-700 ring-2 ring-neutral-200 transition-colors hover:bg-primary/5 hover:text-primary hover:ring-primary max-sm:px-4 sm:gap-6"
              @click="user.role = 'designer'"
            >
              <FaIcon type="fal" icon="palette" class="text-3xl" />
              <div>
                <p class="m-0">Graphiste</p>
                <p class="m-0 text-sm text-neutral-400">Je cherche des projets</p>
              </div>
              <div class="grow text-right">
                <FaIcon type="fal" icon="chevron-right" />
              </div>
            </button>
          </div>
        </fieldset>
        <fieldset v-else>
          <div v-if="user.role === 'client'" class="row">
            <div
              class="form-group col-12 col-sm-6"
              :class="{ 'form-group-invalid': user.errors['user_contact.first_name'] }"
            >
              <input
                id="new_user_first_name"
                v-model="user.user_contact_attributes.first_name"
                :class="{ 'is-invalid': user.errors['user_contact.first_name'] }"
                type="text"
                placeholder="Prénom"
                name="user[user_contact_attributes][first_name]"
                aria-required="true"
                autocomplete="given-name"
                required="required"
                class="form-control"
              />
              <span v-if="user.errors['user_contact.first_name']" class="invalid-feedback block text-sm">
                {{ user.errors['user_contact.first_name'][0] }}
              </span>
            </div>
            <div class="form-group col-12 col-sm-6" :class="{ 'form-group-invalid': user.errors['user_contact.last_name'] }">
              <input
                id="new_user_last_name"
                v-model="user.user_contact_attributes.last_name"
                :class="{ 'is-invalid': user.errors['user_contact.last_name'] }"
                type="text"
                placeholder="Nom de famille"
                name="user[user_contact_attributes][last_name]"
                aria-required="true"
                autocomplete="family-name"
                required="required"
                class="form-control"
              />
              <span v-if="user.errors['user_contact.last_name']" class="invalid-feedback block text-sm">
                {{ user.errors['user_contact.last_name'][0] }}
              </span>
            </div>
          </div>
          <div class="form-group" :class="{ 'form-group-invalid': user.errors.email }">
            <input
              id="new_user_email"
              v-model="user.email"
              :class="{ 'is-invalid': user.errors.email }"
              type="text"
              placeholder="Adresse email"
              name="user[email]"
              autocomplete="email"
              class="form-control"
            />
            <span v-if="user.errors.email" class="invalid-feedback block text-sm">{{ user.errors.email[0] }}</span>
          </div>
          <div v-if="user.role === 'client'" class="row">
            <div
              class="form-group col-12 col-sm-6"
              :class="{ 'form-group-invalid': user.errors['user_contact.country_code'] }"
            >
              <select
                id="new_user_country_code"
                v-model="user.user_contact_attributes.country_code"
                :class="{ 'is-invalid': user.errors['user_contact.country_code'] }"
                name="user[user_contact_attributes][country_code]"
                aria-required="true"
                autocomplete="country"
                required="required"
                class="form-control"
              >
                <option v-for="(country, index) in $root.countryCollection" :key="index" :value="country[1]">
                  {{ country[0] }}
                </option>
              </select>
              <span v-if="user.errors['user_contact.country_code']" class="invalid-feedback block text-sm">
                {{ user.errors['user_contact.country_code'][0] }}
              </span>
            </div>
            <div class="form-group col-12 col-sm-6" :class="{ 'form-group-invalid': user.errors['user_contact.zip_code'] }">
              <input
                id="new_user_zip_code"
                v-model="user.user_contact_attributes.zip_code"
                :class="{ 'is-invalid': user.errors['user_contact.zip_code'] }"
                type="text"
                placeholder="Code postal"
                name="user[user_contact_attributes][zip_code]"
                aria-required="true"
                autocomplete="postal-code"
                required="required"
                class="form-control"
              />
              <span v-if="user.errors['user_contact.zip_code']" class="invalid-feedback block text-sm">
                {{ user.errors['user_contact.zip_code'][0] }}
              </span>
            </div>
          </div>
          <div v-if="user.role === 'designer'" class="form-group" :class="{ 'form-group-invalid': user.errors.username }">
            <input
              id="new_user_username"
              v-model="user.username"
              :class="{ 'is-invalid': user.errors.username }"
              type="text"
              placeholder="Nom d'utilisateur"
              name="user[username]"
              class="form-control"
            />
            <span class="block text-sm">Utilisez uniquement des lettres, des chiffres, et '_'.</span>
            <span v-if="user.errors.username" class="invalid-feedback block text-sm">{{ user.errors.username[0] }}</span>
          </div>
          <div class="form-group" :class="{ 'form-group-invalid': user.errors.password }">
            <input
              id="user_password"
              v-model="user.password"
              :class="{ 'is-invalid': user.errors.password }"
              type="password"
              placeholder="Mot de passe"
              name="user[password]"
              autocomplete="current-password"
              class="form-control"
            />
            <span v-if="user.errors.password" class="invalid-feedback block text-sm">{{ user.errors.password[0] }}</span>
          </div>
          <div class="form-group" :class="{ 'form-group-invalid': user.errors.terms_of_service }">
            <div class="form-check">
              <input
                id="user_terms_of_service"
                v-model="user.terms_of_service"
                :class="{ 'is-invalid': user.errors.terms_of_service }"
                type="checkbox"
                name="user[terms_of_service]"
                aria-required="true"
                required="required"
                class="form-check-input"
              />
              <label for="user_terms_of_service" class="form-check-label font-normal text-gray">
                J'accepte les
                <a href="/pages/terms/general" target="_blank" class="text-gray underline">CGU</a>
                et la
                <a href="/pages/politique-de-confidentialite" target="_blank" class="text-gray underline">
                  politique de confidentialité
                </a>
              </label>
            </div>
            <span v-if="user.errors.terms_of_service" class="invalid-feedback block text-sm">
              {{ user.errors.terms_of_service[0] }}
            </span>
            <Teleport v-if="user.role !== ''" to="#gdpr-note">
              <p class="small mb-6 px-6 text-justify text-muted">
                La société Codeur SAS qui édite Graphiste.com collecte ces informations afin de créer votre compte et de vous
                permettre d’utiliser la plateforme. Pour en savoir plus sur la gestion de vos données personnelles et pour
                exercer vos droits, vous pouvez consulter notre
                <a href="/pages/politique-de-confidentialite" target="_blank" class="text-gray underline">
                  politique de confidentialité
                </a>
                .
              </p>
            </Teleport>
          </div>

          <vue-ladda type="submit" class="btn btn-primary btn-block" :loading="loading">S'inscrire</vue-ladda>
        </fieldset>
      </form>
    </div>
  </div>
</template>

<script>
import { useApi } from '@/apps/shared/composables'
import FormTitle from './FormTitle.vue'

export default {
  components: {
    FormTitle
  },
  props: {
    defaultRole: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      loading: false,
      user: {
        role: '',
        user_contact_attributes: {
          first_name: '',
          last_name: '',
          country_code: '',
          zip_code: ''
        },
        email: '',
        username: '',
        password: '',
        terms_of_service: false,
        errors: {}
      }
    }
  },
  computed: {
    roleName() {
      if (this.user.role === 'client') return 'Client'
      if (this.user.role === 'designer') return 'Graphiste'
      return ''
    }
  },
  watch: {
    defaultRole(newValue) {
      this.user.role = newValue
    }
  },
  methods: {
    signup(e) {
      e.preventDefault()
      this.loading = true
      this.user.errors = { user_contact: {} }
      const userData = { user: { ...this.user } }
      delete userData.user.errors
      useApi().post('/signup', userData).then(this.successCallback, this.errorCallback)
    },
    successCallback(data) {
      this.loading = false
      if (this.user.role !== 'client') return window.location.reload()

      const params = new URLSearchParams(window.location.search)
      params.set('inscription', 'client')
      window.location.search = params
    },
    errorCallback(error) {
      this.loading = false
      this.user.errors = error.response.data.errors
    }
  }
}
</script>
